import React from "react";
import CountUp from "react-countup";
import SVGRenderer from "../SVGRenderer";
import LazyImage from "components/elements/LazyImage/LazyImage";
import SmartLink from "components/SmartLink";
import Button from "components/elements/Button/Button";

import styles from "./Card.module.scss";

/**
 * Standard card layout with image on top and content below
 * 
 * @param {Object} props - Component properties
 */
const CardStandard = React.forwardRef(({ 
	inView,
	title, 
	svg, 
	lead, 
	desc, 
	cta_text, 
	cta_link, 
	image, 
	image_alt, 
	bg_image, 
	bg_image_alt, 
	bg_color, 
	className = "", 
	theme, 
	count_up,
	rating,
	footer,
	...otherProps
}, ref) => {
	return (
		<div
			ref={ref}
			{...(theme ? { "data-bs-theme": theme } : {})}
			style={bg_color && { backgroundColor: bg_color }}
			className={`${styles.Card} card h-100 rounded-4 shadow border-0 position-relative overflow-hidden ${className} ${bg_image && "bg-transparent"}`}>
			{image && (
				<div className="position-relative ratio ratio-16x9">
					<LazyImage
						src={image}
						alt={image_alt || title}
						className="card-img-top"
						fill_mode="cover"
					/>
				</div>
			)}
			<div className="card-body p-4 d-flex flex-column">
				{svg && (
					<div className="mb-3 svg-wrapper">
						<SVGRenderer svgString={svg} />
					</div>
				)}
				{inView && count_up && (
					<CountUp
						className="count-up mb-3"
						end={count_up}
						duration={5}
						separator="."
					/>
				)}
				{title && <h3 className="card-title title fs-5 mb-2">{title}</h3>}
				{lead && <p className="lead mb-3">{lead}</p>}
				
				{desc && (
					<p 
						className="card-text desc mb-3"
						dangerouslySetInnerHTML={{ __html: desc }}></p>
				)}
				
				{rating && (
					<div className="rating-display mb-3">
						<div className="rating-stars">
							{Array(5).fill().map((_, idx) => (
								<span 
									key={idx} 
									className={`rating-star ${idx < Math.floor(rating) ? 'filled' : 'empty'}`}>
									&#9733;
								</span>
							))}
						</div>
						<div className="rating-value ms-2">
							{rating}/5
						</div>
					</div>
				)}
				
				{footer && (
					<div className="card-footer-text small text-muted mt-auto mb-3">
						{footer}
					</div>
				)}
				
				<div className="mt-auto">
					 {/* Here we fix the button rendering - additional logging for debugging */}
					{console.log("Button rendering check:", {cta_text, cta_link})}
					{cta_text && cta_link && (
						<SmartLink 
							to={cta_link}
							title={cta_text}>
							<Button
								size={"md"}
								variant={"primary"}
								letterCase="uppercase">
								{cta_text}
							</Button>
						</SmartLink>
					)}
				</div>
			</div>
			{bg_image && (
				<img
					src={bg_image}
					alt={bg_image_alt || ""}
					className="position-absolute w-100 h-100"
					style={{
						objectFit: "cover",
						filter: "blur(15px)",
						zIndex: -1,
						top: 0,
						left: 0,
					}}
					aria-hidden="true"
				/>
			)}
		</div>
	);
});

export default CardStandard;
